import { zodResolver } from "@hookform/resolvers/zod";
import { useUpdateOrderTrackingMutation } from "@/lib/api/apiSlice";
import { getRtkQueryErrorMessage } from "@/lib/error";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ICourier, IOrder } from "muminsmart-utils";
import React from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

interface TrackingFormProps {
  couriers: ICourier[];
  order: IOrder;
  // eslint-disable-next-line no-unused-vars
  onUpdated: (order: IOrder) => void;
}

const trackingFormSchema = z.object({
  courierName: z.string().trim().min(2, "Courier name is required"),
  trackingNumber: z.string().trim().min(3, "Tracking number is required"),
});

type TrackingFormValues = z.infer<typeof trackingFormSchema>;

const TrackingForm = ({ order, onUpdated }: TrackingFormProps) => {
  const [loading, setLoading] = React.useState(false);
  const [updateOrderTracking] = useUpdateOrderTrackingMutation();

  const form = useForm<TrackingFormValues>({
    resolver: zodResolver(trackingFormSchema),
    defaultValues: {
      courierName: order.courierName || "",
      trackingNumber: order.trackingNumber || "",
    },
  });

  React.useEffect(() => {
    form.reset({
      courierName: order.courierName || "",
      trackingNumber: order.trackingNumber || "",
    });
  }, [form, order.courierName, order.trackingNumber]);

  const onSubmit = async (values: TrackingFormValues) => {
    setLoading(true);

    try {
      const data = await updateOrderTracking({
        id: order.id,
        trackingNumber: values.trackingNumber.trim(),
        courierName: values.courierName.trim(),
      }).unwrap();

      onUpdated(data);
      form.reset({
        courierName: data.courierName,
        trackingNumber: data.trackingNumber,
      });
      toast.success("Order updated with tracking information.");
    } catch (error: unknown) {
      toast.error(
        getRtkQueryErrorMessage(
          error,
          "An error occurred while updating the order.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-2 rounded-2xl bg-white md:flex-row md:items-start"
      >
        <FormField
          control={form.control}
          name="courierName"
          render={({ field }) => (
            <FormItem className="min-w-0 flex-1">
              <FormControl>
                <Input
                  {...field}
                  placeholder="Courier Name"
                  className="rounded-full border-gray-200 bg-white text-xs text-gray-700"
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="trackingNumber"
          render={({ field }) => (
            <FormItem className="min-w-0 flex-1">
              <FormControl>
                <Input
                  {...field}
                  placeholder="Tracking Number"
                  className="rounded-full border-gray-200 bg-white text-xs text-gray-700"
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          disabled={loading}
          className="rounded-full text-xs"
        >
          {loading ? "Updating..." : "Update"}
        </Button>
      </form>
    </Form>
  );
};

export default TrackingForm;
